import React from 'react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { StaffMember, ReportType } from '../../../types/timeReport';

interface TimeReportFiltersProps {
  startDate: Date;
  endDate: Date;
  staffId: string | null;
  reportType: ReportType;
  staffMembers: StaffMember[];
  onStartDateChange: (date: Date) => void;
  onEndDateChange: (date: Date) => void;
  onStaffChange: (staffId: string | null) => void;
  onReportTypeChange: (type: ReportType) => void;
  onExportCSV: () => void;
  onExportPDF: () => void;
}

const TimeReportFilters: React.FC<TimeReportFiltersProps> = ({
  startDate,
  endDate,
  staffId,
  reportType,
  staffMembers,
  onStartDateChange,
  onEndDateChange,
  onStaffChange,
  onReportTypeChange,
  onExportCSV,
  onExportPDF,
}) => {
  const formatDateForInput = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  };

  const handleQuickFilter = (type: string) => {
    const today = new Date();
    let start: Date;
    let end: Date;

    switch (type) {
      case 'this-week':
        start = startOfWeek(today, { weekStartsOn: 1 }); // Monday
        end = endOfWeek(today, { weekStartsOn: 1 });
        onReportTypeChange('weekly'); // Auto-select weekly view
        break;
      case 'last-week':
        const lastWeek = subDays(today, 7);
        start = startOfWeek(lastWeek, { weekStartsOn: 1 });
        end = endOfWeek(lastWeek, { weekStartsOn: 1 });
        onReportTypeChange('weekly'); // Auto-select weekly view
        break;
      case 'this-month':
        start = startOfMonth(today);
        end = endOfMonth(today);
        onReportTypeChange('monthly'); // Auto-select monthly view
        break;
      case 'last-month':
        const lastMonth = subMonths(today, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        onReportTypeChange('monthly'); // Auto-select monthly view
        break;
      default:
        return;
    }

    onStartDateChange(start);
    onEndDateChange(end);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Report Filters</h2>

      {/* Quick Filter Buttons */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Quick Select
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleQuickFilter('this-week')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            This Week
          </button>
          <button
            onClick={() => handleQuickFilter('last-week')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Last Week
          </button>
          <button
            onClick={() => handleQuickFilter('this-month')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            This Month
          </button>
          <button
            onClick={() => handleQuickFilter('last-month')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Last Month
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Start Date */}
        <div>
          <label htmlFor="start-date" className="block text-sm font-medium text-gray-700 mb-1">
            Start Date
          </label>
          <input
            type="date"
            id="start-date"
            value={formatDateForInput(startDate)}
            onChange={(e) => onStartDateChange(new Date(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* End Date */}
        <div>
          <label htmlFor="end-date" className="block text-sm font-medium text-gray-700 mb-1">
            End Date
          </label>
          <input
            type="date"
            id="end-date"
            value={formatDateForInput(endDate)}
            onChange={(e) => onEndDateChange(new Date(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Staff Filter */}
        <div>
          <label htmlFor="staff-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Staff Member
          </label>
          <select
            id="staff-filter"
            value={staffId || ''}
            onChange={(e) => onStaffChange(e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Staff</option>
            {staffMembers.map((staff) => (
              <option key={staff.id} value={staff.id}>
                {staff.name} - {staff.role}
              </option>
            ))}
          </select>
        </div>

        {/* Report Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Report Type
          </label>
          <div className="flex space-x-2">
            <button
              onClick={() => onReportTypeChange('weekly')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                reportType === 'weekly'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => onReportTypeChange('monthly')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                reportType === 'monthly'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => onReportTypeChange('detailed')}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-md ${
                reportType === 'detailed'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Detailed
            </button>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mt-4 flex justify-end space-x-3">
        <button
          onClick={onExportCSV}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <span className="mr-2">📄</span>
          Export CSV
        </button>
        <button
          onClick={onExportPDF}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <span className="mr-2">📑</span>
          Export PDF
        </button>
      </div>
    </div>
  );
};

export default TimeReportFilters;
